/**
 * The framed text field: label, leading glyph, input, optional trailing control, optional message.
 *
 * The props are wide because they mirror `<input>`'s own surface, which is the one place a wide prop
 * list is the honest interface. What the component OWNS is everything a caller would otherwise have
 * to get right on its own: the frame that carries the focus ring rather than the input, the id that
 * ties label to input, `aria-invalid`, and `aria-describedby` wiring to the message.
 *
 * `error` and `invalid` are separate on purpose, and that separation is a design rule rather than a
 * convenience: a bad credential rings the password field and says why **once**, in the form-level
 * banner. So `invalid` is "ring, no words" and `error` is "ring, and these words".
 *
 * `success` is the third message state — the mockup's `.f-msg.good` ("Hasła są zgodne", screen 4).
 * It lives here rather than beside the field in a screen because the `aria-describedby` wiring is
 * the whole reason this component exists; a message a screen renders itself is a message a screen
 * reader never reaches. `error` wins when both are supplied: a problem outranks reassurance.
 */

import { useId, type KeyboardEvent, type ReactNode, type Ref } from 'react';

import { CheckIcon } from './AuthIcons';
import styles from './FormField.module.css';
import hidden from './visuallyHidden.module.css';

export interface FormFieldProps {
  label: string;
  /**
   * The leading glyph, from `AuthIcons`. Optional: the mockup's profile-completion screen draws
   * the name fields with no glyph at all, and a placeholder mark there would be decoration.
   */
  icon?: (props: { className?: string }) => ReactNode;
  /** A second line in the label, e.g. "· opcjonalnie". `--text-2`, never `--text-3`. */
  labelNote?: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'password' | 'date' | 'time' | 'number';
  placeholder?: string;
  autoComplete?: string;
  /** `<input>` passthroughs the date field needs — the age gate's picker bounds. */
  min?: string;
  max?: string;
  /** The booking form's quarter-hour time and its minute length (`<input step>`). */
  step?: number;
  /** `<input>` passthroughs the recovery-code field needs: a digit keypad, and no room past six. */
  inputMode?: 'numeric';
  maxLength?: number;
  /** A message shown under the field. Implies the invalid ring. */
  error?: string | null;
  /** A confirming message under the field. No ring; ignored while `error` is set. */
  success?: string | null;
  /** The invalid ring with no message of its own — the message lives in the form's banner. */
  invalid?: boolean;
  disabled?: boolean;
  /**
   * The value is shown but cannot be edited — the account screen's sign-in address, which the
   * backend refuses to change (`email_change_unsupported`).
   *
   * NOT `disabled`, and the difference is the whole reason this exists: a disabled input drops out
   * of the tab order, cannot be selected or copied, and announces as unavailable rather than as
   * fixed. A read-only one keeps its place, reads out as read-only, and is still copyable — which
   * is what a trainer wants to do with the address they sign in with.
   */
  readOnly?: boolean;
  required?: boolean;
  /** A control inside the frame, right-aligned. The password reveal toggle, or a match tick. */
  trailing?: ReactNode;
  /**
   * The `<input>` itself, for a caller that has to put focus in it or select its text after an
   * answer — the trial code's field, refused by the checkout, comes back focused and selected.
   */
  inputRef?: Ref<HTMLInputElement>;
  /** Off for a value that is not prose: a code, where every red underline would be a false alarm. */
  spellCheck?: boolean;
  /** Keys the field answers itself: Enter to apply rather than to submit, Escape to step back. */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /**
   * A field standing in a row of a card rather than in a form's column: the frame alone, one
   * control tall. The label is still the input's name, drawn off-screen, and the column's foot
   * margin is gone. The trial code on the Pro card, which shares its row with „Masz kod?".
   */
  inline?: boolean;
  /**
   * The id of a sentence the caller draws beside the field that describes it — `aria-describedby`,
   * beside the field's own message. An inline field's reason sits in its card's own line.
   */
  describedBy?: string;
}

export function FormField({
  label,
  icon: Icon,
  labelNote,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  min,
  max,
  step,
  inputMode,
  maxLength,
  error = null,
  success = null,
  invalid = false,
  disabled = false,
  readOnly = false,
  required = false,
  trailing,
  inputRef,
  spellCheck,
  onKeyDown,
  inline = false,
  describedBy,
}: FormFieldProps) {
  const inputId = useId();
  const messageId = useId();
  const hasError = error !== null && error !== '';
  const hasSuccess = !hasError && success !== null && success !== '';
  const showsInvalid = hasError || invalid;

  return (
    <div className={inline ? styles.fieldInline : styles.field}>
      <label className={inline ? hidden.srOnly : styles.label} htmlFor={inputId}>
        {label}
        {labelNote ? <span className={styles.labelNote}>{labelNote}</span> : null}
      </label>

      <div
        className={`${styles.frame} ${readOnly ? styles.frameReadOnly : ''} ${
          showsInvalid ? styles.frameInvalid : ''
        }`}
      >
        {Icon ? <Icon className={styles.icon} /> : null}
        <input
          ref={inputRef}
          className={styles.input}
          id={inputId}
          type={type}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          spellCheck={spellCheck}
          onKeyDown={onKeyDown}
          aria-invalid={showsInvalid}
          // Only when there is something to describe: pointing at an empty element makes some
          // screen readers announce the field's name twice and nothing else.
          aria-describedby={
            [hasError || hasSuccess ? messageId : undefined, describedBy].filter(Boolean).join(' ') ||
            undefined
          }
          onChange={(event) => onChange(event.target.value)}
        />
        {trailing}
      </div>

      {hasError ? (
        <p className={styles.message} id={messageId}>
          {error}
        </p>
      ) : null}

      {hasSuccess ? (
        <p className={`${styles.message} ${styles.messageGood}`} id={messageId}>
          <CheckIcon className={styles.messageIcon} />
          {success}
        </p>
      ) : null}
    </div>
  );
}
